import { Button, Modal } from "antd";
import { Check, FileText, Stethoscope, User, X } from "lucide-react";
import { getOrderStatus, getStatusBadgeClass, REPORT_STATUS } from "./reportStatus";

export const SelectTestOrderModal = ({
  open,
  selectableTestOrders,
  onCancel,
  onSelectOrder,
}) => (
  <Modal
    title={
      <div className="flex items-center space-x-3 text-xl font-bold text-slate-800 pb-4 border-b border-slate-100">
        <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
          <FileText className="text-white text-sm" />
        </div>
        <span>Select Test Orders</span>
      </div>
    }
    open={open}
    onCancel={onCancel}
    footer={null}
    className="custom-modal"
    width={600}
  >
    <div className="py-6">
      {selectableTestOrders.length > 0 ? (
        <div className="space-y-3">
          {selectableTestOrders.map((order) => {
            const status = getOrderStatus(order);

            return (
              <div key={order._id} className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100 hover:shadow-lg transition-shadow duration-200 hover:bg-white">
                <div className="grid items-center justify-between grid-cols-2 mb-2">
                  <p className="text-slate-800 font-medium text-start w-full"><span className="font-semibold mr-2">Order ID: </span>{order.testOrderID || order.orderID || "<No Order ID>"}</p>
                  <p className="text-slate-800 font-medium"><span className="font-semibold mr-2">Name: </span>{order.patient?.patientName || "<No Name>"}</p>
                  <p className="text-slate-800 font-medium"><span className="font-semibold mr-2">Gender: </span>{order.patient?.gender || "<No Gender>"}</p>
                  <p className="text-slate-800 font-medium"><span className="font-semibold mr-2">Mobile: </span>{order.patient?.mobile || "<No Mobile>"}</p>
                  <p className="text-slate-800 font-medium"><span className="font-semibold mr-2">Age: </span>{order.patient?.age || "<No Age>"}</p>
                  <p className="text-slate-800 font-medium"><span className="font-semibold mr-2">Doctor: </span>{order.doctor?.docName || order.doctorName || "<No Doctor>"}</p>
                  <p className="text-slate-800 font-medium"><span className="font-semibold mr-2">Status: </span>
                    <span className={`inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full capitalize ${getStatusBadgeClass(status)}`}>
                      {status === REPORT_STATUS.COMPLETED ? <Check className="w-3 h-3 mr-1" /> : <X className="w-3 h-3 mr-1" />}
                      {status}
                    </span>
                  </p>
                </div>
                <p className="text-sm text-gray-600">Test:{" "}
                  {order.tests && order.tests.length > 0 ? (
                    order.tests.map((t, idx) => (
                      <span key={idx} className="inline-block bg-white px-2 py-1 rounded-full text-xs font-medium text-slate-700 mr-2 mt-1 shadow-sm">
                        {typeof t?.test === "object" ? (t?.test?.title || "N/A") : String(t?.test || t || "N/A")}
                      </span>
                    ))
                  ) : (
                    <span className="text-red-600 font-semibold"> No test found</span>
                  )}
                </p>
                <p className="mt-3">
                  <Button
                    type="primary"
                    onClick={() => onSelectOrder(order)}
                    className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-semibold px-4 py-0 rounded-xl border-0 shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    Select This Order
                  </Button>
                </p>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-8">
          <div className="text-gray-400 mb-4">
            <FileText className="w-12 h-12 mx-auto" />
          </div>
          <p className="text-gray-600">No test orders available for selection.</p>
        </div>
      )}
    </div>
  </Modal>
);

export const PatientInfoModal = ({
  open,
  selectedPatient,
  selectedTestOrder,
  onClose,
}) => (
  <Modal
    open={open}
    onCancel={onClose}
    onOk={onClose}
    closeIcon={false}
    title={
      <div className="bg-gradient-to-r from-purple-500 to-pink-600 p-4">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
            <User className="text-white text-lg" />
          </div>
          <h2 className="text-2xl font-bold text-white">Patient Information</h2>
        </div>
      </div>
    }
  >
    <div className="p-4">
      <div className="space-y-2">
        <InfoRow label="ID" value={selectedPatient?._id || "102"} />
        <InfoRow label="Name" value={selectedPatient?.patientName || "Not Found"} tone="blue" />
        <InfoRow label="Gender" value={selectedPatient?.gender || "Not Found"} tone="green" />
        <InfoRow label="Age" value={selectedPatient?.age || "Not Found"} tone="orange" />
        <InfoRow label="Ref by" value={selectedPatient?.agent || selectedTestOrder?.agent?.name || "Not Specified"} tone="teal" />
      </div>
    </div>
  </Modal>
);

export const GroupedTestModal = ({
  open,
  groupedTests,
  onClose,
}) => (
  <Modal
    open={open}
    onCancel={onClose}
    onOk={onClose}
    title={
      <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-4">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
            <Stethoscope className="text-white text-lg" />
          </div>
          <h2 className="text-2xl font-bold text-white">Test Groups</h2>
        </div>
      </div>
    }
  >
    <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl border border-white/50 overflow-hidden">
      <div className="p-6 max-h-96">
        <div className="overflow-x-auto overflow-y-auto h-96">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-gray-100">
                <th className="text-left py-4 px-4 font-semibold text-slate-700">Test Group</th>
                <th className="text-left py-4 px-4 font-semibold text-slate-700">Test Name</th>
                <th className="text-center py-4 px-4 font-semibold text-slate-700">Status</th>
              </tr>
            </thead>
            <tbody>
              {groupedTests ? (
                Object.entries(groupedTests).map(([groupName, tests]) =>
                  tests.map((test, testIdx) => (
                    <tr
                      key={`${groupName}-${testIdx}`}
                      className="border-b border-gray-200 hover:bg-gray-50 transition-colors"
                    >
                      {testIdx === 0 && (
                        <td
                          rowSpan={tests.length}
                          className="py-4 px-4 font-medium text-slate-600 align-top"
                        >
                          {groupName}
                        </td>
                      )}
                      <td className="py-4 px-4 text-slate-600">
                        {typeof test?.test === "object" ? (test?.test?.title || "N/A") : String(test?.test || "N/A")}
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span className={`inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full capitalize ${getStatusBadgeClass(test.status)}`}>
                          {test.status === REPORT_STATUS.COMPLETED ? <Check className="w-3 h-3 mr-1" /> : <X className="w-3 h-3 mr-1" />}
                          {test.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )
              ) : (
                <tr>
                  <td colSpan={3} className="text-center p-5 text-red-600 font-semibold">No test order selected.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </Modal>
);

const toneClasses = {
  blue: "from-blue-50 to-indigo-50",
  green: "from-green-50 to-emerald-50",
  orange: "from-orange-50 to-amber-50",
  teal: "from-teal-50 to-cyan-50",
  gray: "from-gray-50 to-gray-100",
};

const InfoRow = ({ label, value, tone = "gray" }) => (
  <div className={`flex items-center justify-between p-2 bg-gradient-to-r ${toneClasses[tone]} rounded-xl`}>
    <span className="font-semibold text-slate-700">{label}</span>
    <span className="bg-white px-4 py-1 rounded-lg font-medium text-slate-600 shadow-sm truncate">
      {value}
    </span>
  </div>
);
